import {
  ComponentRef,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import Logger from 'utils/Logger';

import { IConfirmAbsenceWarningDTO } from 'shared/domain/dtos';
import {
  IAbsenceWarning,
  TAbsenceWarningChannel,
  TAbsenceWarningFlow,
} from 'shared/domain/entities';
import {
  makeConfirmAbsenceWarningUseCase,
  makeEvaluateAbsenceWarningUseCase,
} from 'shared/domain/useCases';
import { Modal } from 'shared/presentation/components/atoms';
import { Button } from 'shared/presentation/components/molecules';
import { useLocale } from 'shared/presentation/contexts';

import * as S from './styles';

interface IAbsenceWarningModalRef {
  evaluate(params: {
    customerId: number;
    scheduleId?: number;
    channel: TAbsenceWarningChannel;
    flow: TAbsenceWarningFlow;
  }): Promise<boolean>;
}

/**
 * Sao os tres campos da moldura que este componente exibe. Derivados da entidade em vez de
 * redeclarados, para nao divergirem se o contrato do backend mudar. loadErrorMessage fica de fora
 * de proposito: a mensagem de falha ao carregar so aparece quando a chamada falhou, e nesse caso
 * nao existe payload de onde tira-la.
 */
type TAbsenceWarningFrame = Pick<
  IAbsenceWarning,
  'title' | 'acceptanceLabel' | 'confirmErrorMessage'
>;

const AbsenceWarningModal: React.ForwardRefRenderFunction<
  IAbsenceWarningModalRef,
  unknown
> = (_props, ref) => {
  const modalRef = useRef<ComponentRef<typeof Modal>>(null);
  const resolveRef = useRef<((value: boolean) => void) | null>(null);
  const confirmationParamsRef = useRef<IConfirmAbsenceWarningDTO | null>(null);

  const { t } = useLocale('shared');

  const [message, setMessage] = useState<string | null>(null);
  // Moldura vinda do backend: o aviso tem de ser identico em todos os canais.
  const [frame, setFrame] = useState<TAbsenceWarningFrame | null>(null);
  const [checked, setChecked] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(false);

  const settleEvaluation = (value: boolean) => {
    resolveRef.current?.(value);
    resolveRef.current = null;
  };

  useImperativeHandle(
    ref,
    () => ({
      evaluate: async ({ customerId, scheduleId, channel, flow }) => {
        if (resolveRef.current) return false;

        try {
          const warning = await makeEvaluateAbsenceWarningUseCase().execute({
            customerId,
            scheduleId,
          });

          if (!warning.shouldShow || !warning.moment) return true;

          confirmationParamsRef.current = {
            customerId,
            moment: warning.moment,
            channel,
            flow,
            scheduleId,
          };
          setMessage(warning.message);
          setFrame({
            title: warning.title,
            acceptanceLabel: warning.acceptanceLabel,
            confirmErrorMessage: warning.confirmErrorMessage,
          });
          setChecked(false);
          setError(false);

          return await new Promise<boolean>(resolve => {
            resolveRef.current = resolve;
            modalRef.current?.open();
          });
        } catch (e) {
          Logger.error(e);

          // Fail-open de propósito: o backend revalida a confirmação pendente
          // antes de persistir agendamento/reagendamento/cancelamento (canal TOTEM),
          // então um erro aqui é só risco de UX, não de regra de negócio.
          return true;
        }
      },
    }),
    [],
  );

  const handleConfirm = async () => {
    if (!confirmationParamsRef.current) return;

    setConfirming(true);
    setError(false);

    try {
      await makeConfirmAbsenceWarningUseCase().execute(
        confirmationParamsRef.current,
      );

      settleEvaluation(true);
      modalRef.current?.close();
    } catch (e) {
      Logger.error(e);
      setError(true);
    } finally {
      setConfirming(false);
    }
  };

  const handleCancel = () => {
    modalRef.current?.close();
  };

  return (
    <Modal
      ref={modalRef}
      closeOnOverlayClick={false}
      onClose={() => settleEvaluation(false)}
    >
      <S.Container>
        <h2>{frame?.title ?? t('components.absenceWarning.title')}</h2>

        <p>{message}</p>

        <S.CheckboxRow>
          <S.Checkbox
            checked={checked}
            onChange={e => setChecked(e.target.checked)}
          />
          <span>
            {frame?.acceptanceLabel ??
              t('components.absenceWarning.checkboxLabel')}
          </span>
        </S.CheckboxRow>

        {error && (
          <S.ErrorText>
            {frame?.confirmErrorMessage ?? t('components.absenceWarning.error')}
          </S.ErrorText>
        )}

        <Button
          variant="primary"
          type="button"
          disabled={!checked}
          loading={confirming}
          onClick={handleConfirm}
        >
          {t('components.absenceWarning.confirm')}
        </Button>

        <Button
          variant="default"
          type="button"
          disabled={confirming}
          onClick={handleCancel}
        >
          {t('components.absenceWarning.cancel')}
        </Button>
      </S.Container>
    </Modal>
  );
};

export default forwardRef(AbsenceWarningModal);
